// OAuth Authentication flow

import { config } from './config'
import { readToken, storeToken, validateToken, retrieveToken, clearToken } from './jwt'
import type { JwtToken } from './jwt'

export interface AuthState {
  isAuthenticated: boolean
  token: JwtToken | null
  userId?: string
}

class AuthManager {
  private state: AuthState = {
    isAuthenticated: false,
    token: null
  }

  private listeners: Set<(state: AuthState) => void> = new Set()

  constructor() {
    this.checkExistingSession()
  }

  /**
   * Check if there's a valid session in localStorage
   */
  checkExistingSession(): boolean {
    const token = retrieveToken()
    if (token && validateToken(token)) {
      this.state = {
        isAuthenticated: true,
        token,
        userId: token.claim.sub as string | undefined
      }
      this.notifyListeners()
      return true
    }
    return false
  }

  /**
   * Start OAuth login flow - redirects to authorization server
   */
  startLogin(): void {
    // Save current URL params before OAuth redirect (to restore after callback)
    this.saveUrlParams()

    const redirectUri = this.getRedirectUri()
    const authUrl = `${config.webUrl}/oauth/authorize/?` +
      `client_id=${config.oauthClientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code`

    window.location.href = authUrl
  }

  /**
   * Handle OAuth callback - exchange code for token
   */
  async handleCallback(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')

    if (!code) {
      return false
    }

    try {
      const redirectUri = this.getRedirectUri()
      const response = await fetch(`${config.serviceUrl}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          auth: {
            code,
            redirect_uri: redirectUri
          }
        })
      })

      if (!response.ok) {
        console.error('Token exchange failed:', response.status)
        return false
      }

      const data = await response.json()
      const token = readToken(data.jwt)

      if (token && validateToken(token)) {
        storeToken(token)
        this.state = {
          isAuthenticated: true,
          token,
          userId: token.claim.sub as string | undefined
        }
        this.notifyListeners()

        // Restore original URL params (garden, lang, debug) and remove code parameter
        const restoredUrl = this.restoreUrlParams()
        window.history.replaceState({}, '', restoredUrl)

        return true
      }
    } catch (error) {
      console.error('Auth callback error:', error)
    }

    return false
  }

  /**
   * Log out - clear token and state
   */
  logout(): void {
    clearToken()
    this.state = {
      isAuthenticated: false,
      token: null
    }
    this.notifyListeners()
  }

  /**
   * Get current auth state
   */
  getState(): AuthState {
    return { ...this.state }
  }

  /**
   * Subscribe to auth state changes
   */
  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getState()))
  }

  private getRedirectUri(): string {
    // Use current URL without query params
    return window.location.origin + window.location.pathname
  }

  /**
   * Save current URL params to sessionStorage before OAuth redirect
   * Preserves: garden, lang, debug, camera (app-specific params)
   */
  private saveUrlParams(): void {
    const params = new URLSearchParams(window.location.search)
    const paramsToSave = ['garden', 'lang', 'debug', 'camera']
    const savedParams: Record<string, string> = {}

    for (const key of paramsToSave) {
      const value = params.get(key)
      if (value !== null) {
        savedParams[key] = value
      }
    }

    if (Object.keys(savedParams).length > 0) {
      sessionStorage.setItem('oauth_return_params', JSON.stringify(savedParams))
    }
  }

  /**
   * Restore URL params after OAuth callback
   * Returns the URL to use (pathname + restored params)
   */
  private restoreUrlParams(): string {
    const savedJson = sessionStorage.getItem('oauth_return_params')
    sessionStorage.removeItem('oauth_return_params')

    if (!savedJson) {
      return window.location.pathname
    }

    try {
      const savedParams = JSON.parse(savedJson) as Record<string, string>
      const params = new URLSearchParams()

      for (const [key, value] of Object.entries(savedParams)) {
        params.set(key, value)
      }

      const queryString = params.toString()
      return queryString
        ? `${window.location.pathname}?${queryString}`
        : window.location.pathname
    } catch {
      return window.location.pathname
    }
  }
}

// Singleton instance
export const auth = new AuthManager()
